import PropTypes from 'prop-types'
import React from 'react'
import { Router, Route, Redirect, Switch } from 'react-router-dom'
import { Provider } from 'react-redux'
import Onboarding from 'pages/Onboarding/container'

import { history } from 'libs/history'
import Login from './pages/Login'
import TwoFactorLogin from './pages/TwoFactorLogin/container'
import { LoginSSO } from './pages/LoginSSO'
import ResetPassword from './pages/ResetPassword'
import CheckEmail from './pages/ResetPassword/CheckEmail'
import InvalidPasswordToken from './components/ResetPassword/InvalidPasswordToken'
import InvalidInviteToken from './pages/InvalidInviteToken/presentation'
import CreateNewPassword from './pages/ResetPassword/CreateNewPassword'
import SignUp from './pages/SignUp'
import CheckVerifyEmail from './pages/SignUp/CheckVerifyEmail.js'
import AccountCompletion from './pages/AccountCompletion'
import Error404 from './pages/Error404'
import { requireLogin } from 'hoc/RequireLogin'
import auth from 'auth'
import ScrollToTop from 'components/ScrollToTop'
import { StoreUtmAndReferrerToCookie } from 'components/StoreUtmAndReferrerToCookie'
import { GlobalToastStyle } from 'libs/toast/style'
import { SemanticUICustomGlobalStyle } from 'styles/style'
import { Maintenance } from 'pages/Maintenance'
import { SuspenseWithTimeout } from 'components/SuspenseWithTimeout'
import { Loader } from 'shared/components/Loader'
import InviteeRegistration from 'pages/InviteeRegistration'
import IntercomOauthHandler from 'pages/OauthHandler/intercom'
import OAuthConnect from 'pages/OAuthConnect/container'
import WebEmbedPreview from 'pages/Admin/Brands/WebEmbedPreview'
import CompleteSignUp from 'pages/CompleteSignUp'
import CompleteLoginJit from 'pages/CompleteLoginJit'
import Workspace from 'pages/Workspace'
import SelectWorkspace from 'pages/SelectWorkspace'
import { DemoAccountLoader } from 'organisms/DemoAccountLoader'
import useLoginDemoAccount from 'domains/useLoginDemoAccount'
import { isLoginExpired } from 'domains/authentication/loginExpiration'
import PermissionRoute from 'domains/routes/PermissionRoute'
import constants from 'config/constants'
import useOAuthIframeCompletionListener from 'hooks/useOAuthIframeCompletionListener'
import useMcpConnectResume from 'hooks/useMcpConnectResume'

const AsyncDashboard = React.lazy(() => import('pages/Dashboard'))
const OnboardingWithLogin = requireLogin(Onboarding)

const RedirectExpiredTokenToLoginPage = () => {
  if (isLoginExpired()) {
    auth.logout()
    return <Redirect to="/login/" />
  }
  return null
}

const OAuthIframeCompletionListener = () => {
  useOAuthIframeCompletionListener('/')
  return null
}

const McpConnectResumeListener = () => {
  useMcpConnectResume()
  return null
}

const Routes = ({ store }) => {
  const { shouldLoginDemoAccount, isLoading } = useLoginDemoAccount()

  if (shouldLoginDemoAccount && isLoading) {
    return <DemoAccountLoader />
  }

  return (
    <React.Fragment>
      <GlobalToastStyle />
      <SemanticUICustomGlobalStyle />
      <Provider store={store}>
        <Router history={history}>
          <OAuthIframeCompletionListener />
          <McpConnectResumeListener />
          <ScrollToTop>
            <React.Fragment>
              <StoreUtmAndReferrerToCookie />
              <RedirectExpiredTokenToLoginPage />
              <Route
                exact
                strict
                path="/:url*"
                render={(props) => (
                  <Redirect
                    to={{
                      pathname: `${props.location.pathname}/`,
                      search: props.location.search,
                    }}
                  />
                )}
              />
              <SuspenseWithTimeout fallback={<Loader className="ss-page-loader" />}>
                <Switch>
                  <Route path="/maintenance/" component={Maintenance} />
                  <Route path="/brands/web-embed-preview/" component={WebEmbedPreview} />
                  <Route path="/login/complete-jit/" component={CompleteLoginJit} />
                  <Route path="/login/two-factor/" component={TwoFactorLogin} />
                  <Route path="/login/sso/complete/" component={AccountCompletion} />
                  <Route path="/login/sso/" component={LoginSSO} />
                  <Route path="/login/" component={Login} />
                  <Route path="/signup/complete-sso-signup/" component={AccountCompletion} />
                  <Route path="/signup/complete/" component={CompleteSignUp} />
                  <Route path="/signup/" component={SignUp} />
                  <Route path="/404/" component={Error404} />
                  <Route path="/oauth/intercom/" component={IntercomOauthHandler} />
                  <Route path="/oauth/connect/" component={OAuthConnect} />
                  <Route path="/reset-password" component={ResetPassword} />
                  <Route path="/check-email" component={CheckEmail} />
                  <Route path="/check-verify-email" component={CheckVerifyEmail} />
                  <Route path="/invalid-password-token" component={InvalidPasswordToken} />
                  <Route path="/invalid-invite-token" component={InvalidInviteToken} />
                  <Route path="/create-new-password" component={CreateNewPassword} />
                  <Route path="/create-new-account/:token/" component={InviteeRegistration} />
                  <Route
                    path={[
                      '/dashboards',
                      '/customers',
                      '/team-members',
                      '/reports',
                      '/feedback',
                      '/responses',
                      '/success-dashboard',
                      '/settings',
                      '/profile',
                      '/survey',
                      '/oauth',
                      '/billing',
                    ]}
                    component={Workspace}
                  />
                  <Route path="/workspaces/" component={SelectWorkspace} />
                  <PermissionRoute
                    permission={constants.permissions.viewOnboarding}
                    path="/:workspaceToken/onboarding/"
                    component={OnboardingWithLogin}
                  />
                  <Route path="/:workspaceToken/" render={requireLogin(AsyncDashboard)} />
                  <Route path="/" component={Workspace} />
                </Switch>
              </SuspenseWithTimeout>
            </React.Fragment>
          </ScrollToTop>
        </Router>
      </Provider>
    </React.Fragment>
  )
}

Routes.propTypes = {
  store: PropTypes.object.isRequired,
}

export default Routes



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/routes.js